import { inject, ref, watch, type Ref } from 'vue';

import { stateFactoryKey, type StateFactory } from '@/injectionKeys';
import {
  completeReservationSection,
  createReservationAccordionState,
  openReservationSection,
  resumeReservationAccordionState,
  type ReservationAccordionState,
} from '@/wabunka-src/common/features/Reservation/core/accordion/accordionState';
import { canAutoCompleteReservationSection } from '@/wabunka-src/common/features/Reservation/core/accordion/autoComplete';
import type { FulfillableReservationSectionId, ReservationSectionId } from '@/wabunka-src/common/features/Reservation/core/requiredFields/sections';
import type { WabunkaExperienceEventViewModel } from '@/wabunka-src/features/Experience/models/experienceEvent';

/**
 * 新動線（アコーディオン）のセクション開閉状態。遷移そのものは純関数コア（`core/accordion/`）に委譲する。
 *
 * 開閉は同じ体験イベントの間だけページをまたいで持ち、日程の変更から戻ったときは離れる前のセクションを開く。リロードでは消える（`NON-GOAL-002`）。
 */
export const useReservationAccordion = (experienceEvent: Ref<WabunkaExperienceEventViewModel>, isSectionFulfilled: (sectionId: FulfillableReservationSectionId) => boolean) => {
  const canAutoComplete = (sectionId: ReservationSectionId) => canAutoCompleteReservationSection(sectionId, experienceEvent.value);
  const stateFactory = inject(stateFactoryKey) as StateFactory;
  const lastVisit = stateFactory.create<{ experienceEventId: string; state: ReservationAccordionState } | null>('wabunkaReservationAccordion', null);
  const resumedState = lastVisit.value?.experienceEventId === experienceEvent.value.id ? resumeReservationAccordionState(lastVisit.value.state, isSectionFulfilled) : null;
  const state = ref<ReservationAccordionState>(resumedState ?? createReservationAccordionState(isSectionFulfilled, canAutoComplete));
  watch(
    state,
    (value) => {
      lastVisit.value = { experienceEventId: experienceEvent.value.id, state: value };
    },
    { immediate: true },
  );

  const isOpen = (sectionId: ReservationSectionId) => state.value.openSectionId === sectionId;
  const isVisited = (sectionId: ReservationSectionId) => state.value.visitedSectionIds.includes(sectionId);

  const openSection = (sectionId: ReservationSectionId) => {
    state.value = openReservationSection(state.value, sectionId);
  };

  const completeSection = (sectionId: ReservationSectionId) => {
    state.value = completeReservationSection(state.value, sectionId, canAutoComplete);
  };

  return { isOpen, isVisited, openSection, completeSection };
};

export type ReservationAccordionController = ReturnType<typeof useReservationAccordion>;
